import axios from 'axios';
import CSRFService from '../csrfService';
import store from '../../store/store';
import { resetAuth } from '../../store/slices/authSlice';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

const axiosInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// ── Request interceptor ────────────────────────────────────────────────────────
axiosInstance.interceptors.request.use(
  async (config) => {
    // GET requests and the token endpoint itself don't need a CSRF header
    if (config.method === 'get' || config.url === '/security/csrf-token') {
      return config;
    }

    // Attach in-memory CSRF token — never reads from cookies
    const token = CSRFService.getCachedToken() || await CSRFService.getToken();
    if (token) {
      config.headers['X-XSRF-TOKEN'] = token;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ── Response interceptor ───────────────────────────────────────────────────────
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // 401 — clear auth and redirect to login
    if (error.response?.status === 401) {
      store.dispatch(resetAuth());
      if (!window.location.pathname.includes('/auth/login')) {
        window.location.href = '/auth/login';
      }
      return Promise.reject(error);
    }

    // 403 CSRF error — clear cached token, fetch a new one, retry once
    if (
      error.response?.status === 403 &&
      error.response?.data?.message?.includes('CSRF') &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      try {
        CSRFService.clearToken();
        const freshToken = await CSRFService.getToken();
        if (freshToken) {
          originalRequest.headers['X-XSRF-TOKEN'] = freshToken;
        }
        return axiosInstance(originalRequest);
      } catch (retryError) {
        console.error('Failed to refresh CSRF token:', retryError);
        return Promise.reject(retryError);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;